import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import {
  WaitlistListItem,
  waitlistListQuery,
} from "@/app/Waitlists/GraphQL/waitlistListQuery";
import {
  ActivityLogEvent,
  ModelType,
  QueryActivityLogsWhereColumn,
  QueryWaitlistsOrderColumn,
  QueryWaitlistsWhereColumn,
  SortOrder,
  SqlOperator,
} from "@/gql/graphql";
import { formatDate } from "@/lib/Formatters/formatDate";
import { ActivityLogFeed } from "@/app/ActivityLogs/Components/ActivityLogFeed";
import {
  useGqlMutation,
  useSuspenseGqlQuery,
} from "@/lib/GraphQLCodegen/fetcher";
import { userRecordQuery } from "@/app/Users/GraphQL/userRecordQuery";
import {
  DataTable,
  DataTableColDef,
} from "@/lib/Components/DataTable/DataTable";
import { CellContext } from "@/lib/Components/DataTable/types";
import { TrashIcon, PencilIcon } from "@heroicons/react/20/solid";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { WaitlistUserCard } from "@/app/Waitlists/Components/WaitlistUserCard";
import { TimeSinceNowCell } from "@/lib/Components/DataTable/TimeSinceNowCell";
import { Button } from "@/components/catalyst/button";
import { deleteWaitlistMutation } from "@/app/Waitlists/GraphQL/waitlistMutations";
import { WaitlistEditDialog } from "@/app/Waitlists/Components/WaitlistEditDialog";
import { WaitlistMatchesCell } from "@/app/Waitlists/Components/Tables/WaitlistMatchesCell";
import { useTranslations } from "use-intl";
import { useQueryClient } from "@tanstack/react-query";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";

type WaitlistColumnId =
  | "trip"
  | "departure"
  | "matches"
  | "emailed"
  | "created"
  | "quick-actions"
  | "last_sent_at"
  | "actions";

export function UserWaitlistsDialog({ userId }: { userId: string }) {
  const queryClient = useQueryClient();
  const { data } = useSuspenseGqlQuery(userRecordQuery, {
    id: userId,
  });
  const { close } = useDialogState();

  const { user } = data;

  const columns: DataTableColDef<WaitlistListItem, any, WaitlistColumnId>[] = [
    {
      id: "trip",
      header: "Trip",
      cell: TripCell,
    },
    {
      id: "departure",
      header: "Departure",
      cell: DepartureDateCell,
    },
    {
      id: "matches",
      header: "Matches",
      cell: WaitlistMatchesCell,
    },
    {
      id: "last_sent_at",
      header: "Last sent",
      accessorFn: (row) => row.last_sent_at,
      cell: TimeSinceNowCell,
    },
    {
      id: "created",
      header: "Created",
      accessorFn: (row) => row.created_at,
      cell: TimeSinceNowCell,
    },
    {
      id: "actions",
      cell: ActionsCell,
    },
  ];

  return (
    <BaseDialog>
      <div className="space-y-10">
        <WaitlistUserCard user={data.user} />
        <DataTable
          id="waitlist-dialog"
          title="Waitlists"
          searchable={false}
          columnToggles={false}
          accessor={(data) => data.waitlists}
          document={waitlistListQuery}
          columns={columns}
          getQueryVariables={({ pagination }) => {
            return {
              page: pagination.pageIndex,
              first: pagination.pageSize,
              order: [
                {
                  column: QueryWaitlistsOrderColumn.CreatedAt,
                  order: SortOrder.Desc,
                },
              ],
              where: {
                column: QueryWaitlistsWhereColumn.UserId,
                value: user.id,
              },
            };
          }}
        />

        <ActivityLogFeed
          modelId={user.id}
          modelType={ModelType.User}
          variables={{
            where: {
              AND: [
                {
                  column: QueryActivityLogsWhereColumn.SubjectId,
                  value: user.id,
                },
                {
                  column: QueryActivityLogsWhereColumn.SubjectType,
                  value: ModelType.User,
                },
                {
                  column: QueryActivityLogsWhereColumn.Event,
                  operator: SqlOperator.In,
                  value: [ActivityLogEvent.PhoneCall, ActivityLogEvent.Note],
                },
              ],
            },
          }}
          onLogged={() => {
            queryClient.invalidateQueries({
              type: "active",
              queryKey: ["UsersWithWaitlistsList"],
            });

            close();
          }}
        />
      </div>
    </BaseDialog>
  );
}

function ActionsCell({ row }: CellContext<WaitlistListItem, any>) {
  const { mutateAsync } = useGqlMutation(deleteWaitlistMutation);
  const { open } = useDialog(WaitlistEditDialog);

  return (
    <div className="flex gap-x-2">
      <Button
        plain
        onClick={() => {
          open({ waitlist: row.original });
        }}
      >
        <PencilIcon />
      </Button>
      <Button
        plain
        onClick={async () => {
          await mutateAsync({
            id: row.original.id,
          });
        }}
      >
        <TrashIcon />
      </Button>
    </div>
  );
}

function TripCell({ row }: CellContext<WaitlistListItem, any>) {
  const t = useTranslations("city");

  return (
    <div className="flex flex-col space-y-1">
      <div className="text-sm font-medium text-gray-900">
        {row.original.departureCity
          ? row.original.departureCity.name
          : t(`state.${row.original.departure_state}`)}
      </div>

      <div className="flex items-center space-x-2">
        <span className="text-gray-400">→</span>
        <span className="text-sm text-gray-600">
          {row.original.deliveryCity
            ? row.original.deliveryCity.name
            : t(`state.${row.original.delivery_state}`)}
        </span>
      </div>
    </div>
  );
}

function DepartureDateCell({ row }: CellContext<WaitlistListItem, any>) {
  const earliestDate = new Date(row.original.earliest_departure_date);
  const latestDate = new Date(row.original.latest_departure_date);

  // Check if dates are the same day
  const isSameDay = earliestDate.toDateString() === latestDate.toDateString();

  return (
    <div className="text-sm">
      {isSameDay ? (
        <div className="font-medium text-gray-900">
          {formatDate(row.original.earliest_departure_date)}
        </div>
      ) : (
        <div className="space-y-0.5">
          <div className="font-medium text-gray-900">
            {formatDate(row.original.earliest_departure_date)}
          </div>
          <div className="text-xs text-gray-500">
            to {formatDate(row.original.latest_departure_date)}
          </div>
        </div>
      )}
    </div>
  );
}
